import { apiPrefix, assetUrl } from './tenant'
import { setWechatShare } from './wechat-share'

/**
 * SPA 内切卡时同步社交分享 meta。
 * 首屏的 <title> / og / twitter / canonical / #share-cover 由服务端按 URL 注入（apps/server/src/share-meta.ts），
 * 站内点内链切到别的卡后把这组标签一起改掉（抓 DOM 的平台才能拿到当前卡）。规则与服务端一致（SVG 不当封面、摘要去标题首行、截 150）。
 * 微信不读 og 也不抓图（2017 年起），微信内的卡片走 wechat-share.js 的 JS-SDK。
 */

const EXCERPT_MAX = 150
const SVG_RE = /\.svg(\?|#|$)/i
const COVER_RE = /^(\/uploads\/|https?:\/\/)/i

export const isCoverCandidate = (src) => typeof src === 'string' && COVER_RE.test(src) && !SVG_RE.test(src)

/** 递归找 hepta JSON 里第一张可当封面的图 */
const firstImageInDoc = (node) => {
    if (!node || typeof node !== 'object') return null
    if (node.type === 'image' && node.attrs && isCoverCandidate(node.attrs.src)) return node.attrs.src
    if (Array.isArray(node.content)) {
        for (const child of node.content) {
            const hit = firstImageInDoc(child)
            if (hit) return hit
        }
    }
    return null
}

/** 渲染后的 DOM 里第一张可当封面的图：只认 image 节点渲染出的 .imgBox img（与服务端只看 image 节点一致），
 *  正文里 {HTML} 代码块原样渲染出来的 <img>（如 CC 徽章）不算 */
const firstImageInDom = (el) => {
    const imgs = el.querySelectorAll('.imgBox img')
    for (const img of imgs) {
        const src = img.getAttribute('src')
        if (isCoverCandidate(src)) return src
    }
    return null
}

/** 渲染后 DOM 的逐行文本：跳过代码块 / 表格（与服务端 docLines 一致），块元素之间换行 */
const domText = (el) => {
    const clone = el.cloneNode(true)
    clone.querySelectorAll('pre, table').forEach((n) => n.remove())
    clone.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li, blockquote, div, br').forEach((n) => n.insertAdjacentText('beforeend', '\n'))
    return clone.textContent || ''
}

export const makeExcerpt = (text, title, max = EXCERPT_MAX) => {
    if (!text) return ''
    const lines = String(text).split(/\r?\n/)
    const t = (title || '').trim()
    if (t && lines.length && lines[0].trim() === t) lines.shift()
    const s = lines.join(' ').replace(/\s+/g, ' ').trim()
    return s.length <= max ? s : s.slice(0, max).replace(/\s+$/, '') + '…'
}

const toAbsolute = (u) => {
    if (!u) return ''
    try {
        // 站内相对地址（/uploads、/share-default.png）走 assetUrl；apiPrefix 拼出来的已含 apiBase
        return new URL(u.startsWith('/') ? assetUrl(u) : u, window.location.href).href
    } catch {
        return ''
    }
}

/** 静态兜底分享图（apps/web/public/share-default.png） */
export const DEFAULT_SHARE_IMAGE = '/share-default.png'

/**
 * 从 Post.js 的 card 取封面与摘要。card.content 在 findContent 里已被 heptaToMD 换成 HTMLElement；
 * 万一还是 hepta JSON 字符串（正文刚到、尚未渲染）就走 JSON。
 * 封面与服务端注入一致：一律动态画作封面（目录带 coverKey / coverKeySquare）→ 站点分享图 → 静态兜底图。
 * image 是 2:1（og / Twitter），imageSquare 是正方形（微信私聊 / 朋友圈缩略图）。
 */
export const cardShareData = (card, site) => {
    let excerpt = ''
    const content = card && card.content
    if (content && typeof content === 'object' && typeof content.querySelectorAll === 'function') {
        excerpt = makeExcerpt(domText(content), card.title)
    }
    let image = null
    let imageSquare = null
    if (card && card.coverKey) {
        image = apiPrefix() + '/v1/cards/' + card.id + '/cover.png?v=' + card.coverKey
        imageSquare = card.coverKeySquare ? apiPrefix() + '/v1/cards/' + card.id + '/cover.png?v=' + card.coverKeySquare + '&r=square' : image
    }
    if (!image && site && site.shareImageUrl) image = imageSquare = site.shareImageUrl
    if (!image) image = imageSquare = DEFAULT_SHARE_IMAGE
    return { excerpt, image: toAbsolute(image), imageSquare: toAbsolute(imageSquare) }
}

const setMeta = (attr, key, value) => {
    let el = document.head.querySelector(`meta[${attr}="${key}"]`)
    if (!value) {
        if (el) el.remove()
        return
    }
    if (!el) {
        el = document.createElement('meta')
        el.setAttribute(attr, key)
        document.head.appendChild(el)
    }
    el.setAttribute('content', value)
}

const setCanonical = (url) => {
    let el = document.head.querySelector('link[rel="canonical"]')
    if (!url) {
        if (el) el.remove()
        return
    }
    if (!el) {
        el = document.createElement('link')
        el.setAttribute('rel', 'canonical')
        document.head.appendChild(el)
    }
    el.setAttribute('href', url)
}

/** #share-cover：微信取 body 第一张 <img>。放在 #root 之前的 0×0 容器里（服务端注入的同一结构），没有就现建 */
const setCover = (image) => {
    let img = document.getElementById('share-cover')
    if (!image) {
        const box = document.getElementById('share-cover-box')
        if (box) box.remove()
        else if (img) img.remove()
        return
    }
    if (!img) {
        const box = document.createElement('div')
        box.id = 'share-cover-box'
        box.setAttribute('style', 'width:0;height:0;overflow:hidden')
        box.setAttribute('aria-hidden', 'true')
        img = document.createElement('img')
        img.id = 'share-cover'
        img.alt = ''
        box.appendChild(img)
        const root = document.getElementById('root')
        if (root && root.parentNode) root.parentNode.insertBefore(box, root)
        else document.body.insertBefore(box, document.body.firstChild)
    }
    if (img.getAttribute('src') !== image) img.setAttribute('src', image)
}

let lastKey = ''

/** 更新整组分享标签；在 render 体里会被反复调用，按内容去重 */
export const updateShareMeta = ({ title, description, url, image, imageSquare }) => {
    const square = imageSquare || image
    const key = [title, description, url, image, square].join('\n')
    if (key === lastKey) return
    lastKey = key
    setMeta('property', 'og:title', title)
    setMeta('name', 'twitter:title', title)
    setMeta('property', 'og:description', description)
    setMeta('name', 'twitter:description', description)
    setMeta('name', 'description', description)
    setMeta('property', 'og:url', url)
    setCanonical(url)
    setMeta('property', 'og:image', image)
    setMeta('property', 'og:image:alt', image ? title : '')
    setMeta('name', 'twitter:image', image)
    setMeta('name', 'twitter:card', image ? 'summary_large_image' : 'summary')
    setCover(square)
    // 微信不读上面这些标签：在微信内走 JS-SDK 把同一组数据喂给分享接口（未配置时是空操作）
    setWechatShare({ title, description, url, image: square })
}
